
import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Calendar,
  Clock,
  AlertTriangle,
  CheckCircle,
  Plus,
  Eye,
  Trash2,
  Search,
  RefreshCw,
  Filter,
  X,
} from "lucide-react";
import {
  useAgendaPaginated,
  useCriarEventoAgenda,
  useAtualizarEventoAgenda,
  EventoAgenda,
  calcularDiasAteVencimento,
  type AgendaFiltroParams,
  type AgendaUrgenciaFiltro,
} from "@/hooks/useAgenda";
import { podeIniciarRenovacao, mensagemRenovacaoIndisponivel } from "@/lib/renovacaoPrazo";
import { useMarcarPedidoNaoRenovado } from "@/hooks/usePedidos";
import { CriarPedidoRenovacaoDialog } from "./CriarPedidoRenovacaoDialog";
import { toast } from "sonner";
import { VerPedidoModal } from "./VerPedidoModal";
import { ListagemAcoes, type ListagemColumn } from "@/components/listagem/ListagemAcoes";
import { listagemThLeft, listagemThCenter, listagemTdLeft, listagemTdCenter } from "@/components/listagem/listagemTableClasses";
import { cn } from "@/lib/utils";
import { BRASIL_UFS } from "@/lib/estados";
import { useCidadesPorEstado } from "@/hooks/useClientesEstadosCidades";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { formatAgendaCicloLabel } from "@/lib/pedidosCiclo";
import { resolverPedidoIdPorEvento } from "@/lib/resolverPedidoRenovacao";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

function formatDataBr(iso?: string | null) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("pt-BR");
}

interface NovoEventoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function NovoEventoDialog({ open, onOpenChange }: NovoEventoDialogProps) {
  const criarEvento = useCriarEventoAgenda();
  const [formData, setFormData] = useState({
    titulo: '',
    descricao: '',
    data_inicio: '',
    data_fim: '',
    tipo: 'outro' as const,
    cliente_id: '',
    prioridade: 'media' as const
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      await criarEvento.mutateAsync({
        titulo: formData.titulo,
        descricao: formData.descricao,
        data_inicio: formData.data_inicio,
        data_fim: formData.data_fim || undefined,
        tipo: formData.tipo,
        cliente_id: formData.cliente_id ? parseInt(formData.cliente_id) : undefined,
        prioridade: formData.prioridade
      });

      toast.success("Evento criado com sucesso!");
      onOpenChange(false);
      setFormData({
        titulo: '',
        descricao: '',
        data_inicio: '',
        data_fim: '',
        tipo: 'outro',
        cliente_id: '',
        prioridade: 'media'
      });
    } catch (error) {
      console.error('Erro ao criar evento:', error);
      toast.error("Erro ao criar evento");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Novo Evento na Agenda</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="titulo">Título</Label>
            <Input
              id="titulo"
              value={formData.titulo}
              onChange={(e) => setFormData({...formData, titulo: e.target.value})}
              required
            />
          </div>

          <div>
            <Label htmlFor="descricao">Descrição</Label>
            <Textarea
              id="descricao"
              value={formData.descricao}
              onChange={(e) => setFormData({...formData, descricao: e.target.value})}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="data_inicio">Data de Início</Label>
              <Input
                id="data_inicio"
                type="date"
                value={formData.data_inicio}
                onChange={(e) => setFormData({...formData, data_inicio: e.target.value})}
                required
              />
            </div>

            <div>
              <Label htmlFor="data_fim">Data de Fim (opcional)</Label>
              <Input
                id="data_fim"
                type="date"
                value={formData.data_fim}
                onChange={(e) => setFormData({...formData, data_fim: e.target.value})}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="tipo">Tipo</Label>
              <Select
                value={formData.tipo}
                onValueChange={(value) => setFormData({...formData, tipo: value as any})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="renovacao_seguro">Renovação de Seguro</SelectItem>
                  <SelectItem value="vencimento_contrato">Vencimento de Contrato</SelectItem>
                  <SelectItem value="outro">Outro</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="prioridade">Prioridade</Label>
              <Select
                value={formData.prioridade}
                onValueChange={(value) => setFormData({...formData, prioridade: value as any})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="baixa">Baixa</SelectItem>
                  <SelectItem value="media">Média</SelectItem>
                  <SelectItem value="alta">Alta</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="cliente_id">Cliente ID (opcional)</Label>
            <Input
              id="cliente_id"
              type="number"
              value={formData.cliente_id}
              onChange={(e) => setFormData({...formData, cliente_id: e.target.value})}
              placeholder="ID do cliente (opcional)"
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={criarEvento.isPending}>
              {criarEvento.isPending ? "Criando..." : "Criar Evento"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function Agenda() {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;
  const [buscaEventoInput, setBuscaEventoInput] = useState(""); // Estado local para o input
  const [buscaEvento, setBuscaEvento] = useState(""); // Estado para a query (com debounce)

  const filtrosDraftDefault = useMemo(
    () => ({
      dataInicio: "",
      dataFim: "",
      status: "todos",
      prioridade: "todas",
      estado: "",
      cidade: "",
      urgencia: "" as "" | NonNullable<AgendaUrgenciaFiltro>,
    }),
    []
  );
  const [filtrosDraft, setFiltrosDraft] = useState(filtrosDraftDefault);
  const [filtrosAplicados, setFiltrosAplicados] = useState(filtrosDraftDefault);
  const { data: cidadesDoEstado = [] } = useCidadesPorEstado(filtrosDraft.estado);

  const filtrosHook = useMemo<AgendaFiltroParams>(() => {
    const periodo =
      filtrosAplicados.dataInicio || filtrosAplicados.dataFim
        ? { dataInicio: filtrosAplicados.dataInicio || undefined, dataFim: filtrosAplicados.dataFim || undefined }
        : null;
    return {
      periodo,
      status: filtrosAplicados.status === "todos" ? null : filtrosAplicados.status,
      prioridade: filtrosAplicados.prioridade === "todas" ? null : filtrosAplicados.prioridade,
      estado: filtrosAplicados.estado || null,
      cidade: filtrosAplicados.cidade || null,
      urgencia: (filtrosAplicados.urgencia || null) as AgendaUrgenciaFiltro,
    };
  }, [filtrosAplicados]);

  const filtrosAtivosCount = useMemo(() => {
    let n = 0;
    if (buscaEvento.trim()) n += 1;
    if (filtrosAplicados.dataInicio || filtrosAplicados.dataFim) n += 1;
    if (filtrosAplicados.status !== "todos") n += 1;
    if (filtrosAplicados.prioridade !== "todas") n += 1;
    if (filtrosAplicados.estado) n += 1;
    if (filtrosAplicados.cidade) n += 1;
    if (filtrosAplicados.urgencia) n += 1;
    return n;
  }, [buscaEvento, filtrosAplicados]);
  
  // Debounce: atualizar busca após 700ms sem digitação (reduz significativamente requisições ao banco)
  useEffect(() => {
    // Se o campo estiver vazio, limpar busca imediatamente
    if (buscaEventoInput === "") {
      setBuscaEvento("");
      return;
    }

    // Para campos com texto, aguardar delay para evitar queries excessivas
    const timer = setTimeout(() => {
      setBuscaEvento(buscaEventoInput);
    }, 700);

    return () => clearTimeout(timer);
  }, [buscaEventoInput]);
  
  // Resetar para página 1 quando busca ou filtro mudar
  useEffect(() => {
    setCurrentPage(1);
  }, [buscaEvento, filtrosHook]);
  
  // Buscar eventos paginados e total
  const { data: eventosResult, isLoading, error } = useAgendaPaginated(
    currentPage, 
    itemsPerPage, 
    buscaEvento,
    false,
    filtrosHook
  );
  const eventos = eventosResult?.data || [];
  const totalEventos = eventosResult?.count || 0;
  const [showNovoEvento, setShowNovoEvento] = useState(false);
  const [eventoSelecionado, setEventoSelecionado] = useState<EventoAgenda | null>(null);
  const [eventoRenovacao, setEventoRenovacao] = useState<EventoAgenda | null>(null);
  const [showCriarRenovacao, setShowCriarRenovacao] = useState(false);
  const [eventoNaoRenovar, setEventoNaoRenovar] = useState<EventoAgenda | null>(null);
  const [motivoNaoRenovar, setMotivoNaoRenovar] = useState("");
  const [salvandoNaoRenovar, setSalvandoNaoRenovar] = useState(false);
  const atualizarEvento = useAtualizarEventoAgenda();
  const marcarPedidoNaoRenovado = useMarcarPedidoNaoRenovado();
  const { isVendedorInterno, isVendedorScoped } = useAuth();
  const somenteEscopoVendedor = isVendedorInterno() || isVendedorScoped();

  const agendaColumns: ListagemColumn<EventoAgenda & { dias: number | null; cliente_nome: string; tipo_label: string }>[] = [
    { key: "id", label: "ID", value: (e) => e.id },
    { key: "cliente", label: "Cliente", value: (e) => (e as any).cliente_nome ?? "" },
    { key: "venda", label: "Venda", value: (e) => (e as any).tipo_label ?? "" },
    { key: "created_at", label: "Data de criação", value: (e) => formatDataBr(e.created_at) },
    { key: "data_fim", label: "Data Fim", value: (e) => e.data_fim ?? "" },
    { key: "dias", label: "Dias até vencimento", value: (e) => (e as any).dias ?? "" },
    { key: "status", label: "Status", value: (e) => e.status },
  ];
  const [visibleAgendaCols, setVisibleAgendaCols] = useState<string[]>([
    "id",
    "cliente",
    "venda",
    "created_at",
    "data_fim",
    "dias",
    "status",
  ]);
  const toggleAgendaCol = (key: string, nextVisible: boolean) => {
    setVisibleAgendaCols((prev) => (nextVisible ? Array.from(new Set([...prev, key])) : prev.filter((k) => k !== key)));
  };

  const handleAplicarFiltros = () => {
    setFiltrosAplicados(filtrosDraft);
    setBuscaEvento(buscaEventoInput);
    setCurrentPage(1);
  };

  const handleLimparFiltros = () => {
    setFiltrosDraft(filtrosDraftDefault);
    setFiltrosAplicados(filtrosDraftDefault);
    setBuscaEventoInput("");
    setBuscaEvento("");
    setCurrentPage(1);
  };

  const aplicarUrgenciaRapida = (urgencia: "" | NonNullable<AgendaUrgenciaFiltro>) => {
    const next = { ...filtrosDraft, urgencia };
    setFiltrosDraft(next);
    setFiltrosAplicados(next);
    setCurrentPage(1);
  };

  const getTipoLabel = (evento: EventoAgenda) => {
    if (evento.tipo === "renovacao_seguro") {
      return formatAgendaCicloLabel(evento.numero_ciclo);
    }
    return evento.tipo.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const getStatusBadgeClass = (status: string) => {
    if (status === "concluido") return "bg-gray-800 text-white hover:bg-gray-800";
    if (status === "cancelado") return "bg-gray-200 text-gray-700 hover:bg-gray-200";
    return "bg-gray-100 text-gray-800 hover:bg-gray-100";
  };

  const handleStatusChange = async (
    eventoId: number,
    novoStatus: 'pendente' | 'concluido' | 'cancelado',
  ) => {
    try {
      await atualizarEvento.mutateAsync({
        id: eventoId,
        status: novoStatus
      });
      toast.success("Status atualizado com sucesso!");
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      toast.error("Erro ao atualizar status");
    }
  };

  const handleConfirmarNaoRenovar = async () => {
    if (!eventoNaoRenovar) return;
    const motivo = motivoNaoRenovar.trim();
    if (!motivo) {
      toast.error("Informe o motivo da não renovação.");
      return;
    }
    setSalvandoNaoRenovar(true);
    try {
      const pedidoId =
        eventoNaoRenovar.pedido_id ||
        (await resolverPedidoIdPorEvento(eventoNaoRenovar));
      if (!pedidoId) {
        throw new Error("Não foi possível identificar o pedido deste evento.");
      }
      await marcarPedidoNaoRenovado.mutateAsync({ pedidoId, motivo });
      await atualizarEvento.mutateAsync({
        id: eventoNaoRenovar.id,
        status: "cancelado",
      });
      toast.success("Pedido enviado para Não renovados");
      setEventoNaoRenovar(null);
      setMotivoNaoRenovar("");
    } catch (e) {
      console.error(e);
      toast.error((e as Error)?.message || "Erro ao marcar como não renovado");
    } finally {
      setSalvandoNaoRenovar(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p>Carregando agenda...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="text-red-600 mb-4">
            <AlertTriangle className="h-8 w-8 mx-auto" />
          </div>
          <p className="text-red-600">Erro ao carregar agenda</p>
          <p className="text-sm text-gray-600 mt-2">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-1">Agenda de Renovações</h2>
          <p className="text-sm text-gray-600">Gerencie eventos e renovações de seguro</p>
        </div>
        {!somenteEscopoVendedor && (
          <Button onClick={() => setShowNovoEvento(true)} className="bg-green-600 hover:bg-green-700 text-white">
            <Plus className="h-4 w-4 mr-2" />
            Novo Evento
          </Button>
        )}
      </div>

      <Card className="border-0 shadow-md">
        <CardContent className="p-0">
          <div className="rounded-lg bg-[#C9C9C9] p-4 text-xs">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-800">
                <Filter className="h-4 w-4" />
                Filtros
                {filtrosAtivosCount > 0 && (
                  <Badge className="bg-gray-800 text-white text-[10px] px-1.5 py-0">
                    {filtrosAtivosCount} ativo{filtrosAtivosCount > 1 ? "s" : ""}
                  </Badge>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {(
                  [
                    { value: "", label: "Todas" },
                    { value: "proximas30", label: "30 dias" },
                    { value: "urgentes7", label: "7 dias" },
                    { value: "vencidas", label: "Vencidas" },
                  ] as const
                ).map((opt) => (
                  <Button
                    key={opt.value || "todas"}
                    type="button"
                    size="sm"
                    variant={filtrosAplicados.urgencia === opt.value ? "default" : "outline"}
                    className={cn(
                      "h-7 px-2.5 text-xs",
                      filtrosAplicados.urgencia === opt.value && "bg-gray-900 text-white hover:bg-gray-800"
                    )}
                    onClick={() => aplicarUrgenciaRapida(opt.value)}
                  >
                    {opt.label}
                  </Button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-12 items-end">
              <div className="md:col-span-3">
                <Label className="text-xs">Buscar</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                  <Input
                    placeholder="Título, cliente ou descrição..."
                    value={buscaEventoInput}
                    onChange={(e) => setBuscaEventoInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAplicarFiltros();
                      }
                    }}
                    className="h-8 pl-9 text-xs"
                  />
                </div>
              </div>

              <div className="md:col-span-2">
                <Label className="text-xs">Vencimento - início</Label>
                <Input
                  type="date"
                  className="h-8 text-xs"
                  value={filtrosDraft.dataInicio}
                  onChange={(e) => setFiltrosDraft((p) => ({ ...p, dataInicio: e.target.value }))}
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Vencimento - fim</Label>
                <Input
                  type="date"
                  className="h-8 text-xs"
                  value={filtrosDraft.dataFim}
                  onChange={(e) => setFiltrosDraft((p) => ({ ...p, dataFim: e.target.value }))}
                />
              </div>

              <div className="md:col-span-1">
                <Label className="text-xs">Status</Label>
                <Select
                  value={filtrosDraft.status}
                  onValueChange={(v) => {
                    const next = { ...filtrosDraft, status: v };
                    setFiltrosDraft(next);
                    setFiltrosAplicados(next);
                    setCurrentPage(1);
                  }}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos</SelectItem>
                    <SelectItem value="pendente">Pendente</SelectItem>
                    <SelectItem value="concluido">Concluído</SelectItem>
                    <SelectItem value="cancelado">Cancelado</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="md:col-span-1">
                <Label className="text-xs">Prioridade</Label>
                <Select
                  value={filtrosDraft.prioridade}
                  onValueChange={(v) => {
                    const next = { ...filtrosDraft, prioridade: v };
                    setFiltrosDraft(next);
                    setFiltrosAplicados(next);
                    setCurrentPage(1);
                  }}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todas">Todas</SelectItem>
                    <SelectItem value="baixa">Baixa</SelectItem>
                    <SelectItem value="media">Média</SelectItem>
                    <SelectItem value="alta">Alta</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="md:col-span-1">
                <Label className="text-xs">Estado</Label>
                <Select
                  value={filtrosDraft.estado || "todos"}
                  onValueChange={(v) => {
                    const next = {
                      ...filtrosDraft,
                      estado: v === "todos" ? "" : v,
                      cidade: "",
                    };
                    setFiltrosDraft(next);
                    setFiltrosAplicados(next);
                    setCurrentPage(1);
                  }}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos</SelectItem>
                    {BRASIL_UFS.map((uf) => (
                      <SelectItem key={uf.sigla} value={uf.sigla}>
                        {uf.sigla}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="md:col-span-2">
                <Label className="text-xs">Cidade</Label>
                <Select
                  value={filtrosDraft.cidade || "todas"}
                  onValueChange={(v) => {
                    const next = { ...filtrosDraft, cidade: v === "todas" ? "" : v };
                    setFiltrosDraft(next);
                    setFiltrosAplicados(next);
                    setCurrentPage(1);
                  }}
                  disabled={!filtrosDraft.estado}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue placeholder={filtrosDraft.estado ? "Todas" : "Selecione UF"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todas">Todas</SelectItem>
                    {cidadesDoEstado.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap gap-1.5 min-h-[28px]">
                {filtrosAplicados.urgencia === "proximas30" && (
                  <Badge variant="outline" className="bg-white gap-1">
                    Próximas 30 dias
                    <button type="button" onClick={() => aplicarUrgenciaRapida("")} aria-label="Remover filtro">
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                )}
                {filtrosAplicados.urgencia === "urgentes7" && (
                  <Badge variant="outline" className="bg-white gap-1">
                    Urgentes 7 dias
                    <button type="button" onClick={() => aplicarUrgenciaRapida("")} aria-label="Remover filtro">
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                )}
                {filtrosAplicados.urgencia === "vencidas" && (
                  <Badge variant="outline" className="bg-white gap-1">
                    Vencidas
                    <button type="button" onClick={() => aplicarUrgenciaRapida("")} aria-label="Remover filtro">
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                )}
                {filtrosAplicados.status !== "todos" && (
                  <Badge variant="outline" className="bg-white">Status: {filtrosAplicados.status}</Badge>
                )}
                {filtrosAplicados.prioridade !== "todas" && (
                  <Badge variant="outline" className="bg-white">Prioridade: {filtrosAplicados.prioridade}</Badge>
                )}
                {filtrosAplicados.estado && (
                  <Badge variant="outline" className="bg-white">
                    {filtrosAplicados.estado}
                    {filtrosAplicados.cidade ? ` / ${filtrosAplicados.cidade}` : ""}
                  </Badge>
                )}
              </div>
              <div className="flex gap-2">
                <Button className="h-8 px-3 text-xs" variant="secondary" onClick={handleLimparFiltros}>
                  Limpar
                </Button>
                <Button className="h-8 px-3 text-xs bg-green-600 text-white hover:bg-green-700" onClick={handleAplicarFiltros}>
                  Aplicar
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-0 shadow-md">
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <div>
              <CardTitle className="text-gray-900">Lista de Renovações</CardTitle>
              <p className="text-sm text-gray-500 mt-1">Ordenadas pelas mais próximas do vencimento no topo</p>
            </div>
            <ListagemAcoes
              title="Agenda de Renovações"
              subtitle={
                filtrosAplicados.urgencia === "proximas30"
                  ? "Filtro: próximas 30 dias"
                  : filtrosAplicados.urgencia === "urgentes7"
                    ? "Filtro: urgentes 7 dias"
                    : filtrosAplicados.urgencia === "vencidas"
                      ? "Filtro: vencidas"
                      : "Filtro: todas"
              }
              rows={eventos.map((e) => ({
                ...e,
                dias: calcularDiasAteVencimento(e.data_fim),
                cliente_nome: e.cliente?.nome || (e.cliente_id ? `Cliente ${e.cliente_id}` : "—"),
                tipo_label: e.tipo === "renovacao_seguro"
                  ? formatAgendaCicloLabel(e.numero_ciclo)
                  : e.tipo.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()),
              }))}
              columns={agendaColumns}
              visibleKeys={visibleAgendaCols}
              onToggleColumn={toggleAgendaCol}
              filenameBase={`agenda_${filtrosAplicados.urgencia || "todas"}_p${currentPage}`}
              enableExport
              fetchAllRows={async () => {
                const pageSize = 1000;
                let offset = 0;
                const all: any[] = [];
                const clienteFilterPresent = !!(
                  (filtrosHook?.estado && filtrosHook.estado.trim()) || (filtrosHook?.cidade && filtrosHook.cidade.trim())
                );

                // eslint-disable-next-line no-constant-condition
                while (true) {
                  let query = supabase
                    .from("agenda")
                    .select(
                      `
                      *,
                      cliente:clientes${clienteFilterPresent ? "!inner" : ""}(id, nome, cidade, estado),
                      contrato:contratos(id, numero_contrato, pedido:pedidos(id))
                    `
                    )
                    .eq("tipo", "renovacao_seguro")
                    .order("data_fim", { ascending: true });

                  if (filtrosHook?.periodo?.dataInicio) query = query.gte("data_fim", filtrosHook.periodo.dataInicio);
                  if (filtrosHook?.periodo?.dataFim) query = query.lte("data_fim", filtrosHook.periodo.dataFim);
                  if (filtrosHook?.status) query = query.eq("status", filtrosHook.status);
                  if (filtrosHook?.prioridade) query = query.eq("prioridade", filtrosHook.prioridade);
                  if (filtrosHook?.estado) query = query.eq("cliente.estado", filtrosHook.estado.toUpperCase());
                  if (filtrosHook?.cidade) query = query.eq("cliente.cidade", filtrosHook.cidade);

                  if (filtrosHook?.urgencia) {
                    const hoje = new Date();
                    hoje.setHours(0, 0, 0, 0);
                    const hojeStr = hoje.toISOString().split("T")[0];
                    if (filtrosHook.urgencia === "vencidas") {
                      query = query.not("data_fim", "is", null).lt("data_fim", hojeStr);
                    } else {
                      const dias = filtrosHook.urgencia === "urgentes7" ? 7 : 30;
                      const dataLimite = new Date(hoje);
                      dataLimite.setDate(hoje.getDate() + dias);
                      const dataLimiteStr = dataLimite.toISOString().split("T")[0];
                      query = query.not("data_fim", "is", null).gte("data_fim", hojeStr).lte("data_fim", dataLimiteStr);
                    }
                  }

                  if (buscaEvento && buscaEvento.trim()) {
                    const searchLower = buscaEvento.trim().toLowerCase();
                    if (!isNaN(Number(buscaEvento.trim()))) {
                      query = query.eq("id", parseInt(buscaEvento.trim()));
                    } else {
                      const { data: clientesEncontrados } = await supabase.from("clientes").select("id").ilike("nome", `%${searchLower}%`);
                      if (clientesEncontrados && clientesEncontrados.length > 0) {
                        const clienteIds = clientesEncontrados.map((c: any) => c.id);
                        query = query.or(
                          `titulo.ilike.%${searchLower}%,descricao.ilike.%${searchLower}%,tipo.ilike.%${searchLower}%,status.ilike.%${searchLower}%,cliente_id.in.(${clienteIds.join(
                            ","
                          )})`
                        );
                      } else {
                        query = query.or(`titulo.ilike.%${searchLower}%,descricao.ilike.%${searchLower}%,tipo.ilike.%${searchLower}%,status.ilike.%${searchLower}%`);
                      }
                    }
                  }

                  const { data, error } = await query.range(offset, offset + pageSize - 1);
                  if (error) throw error;
                  const rows = data || [];
                  if (rows.length === 0) break;
                  all.push(...rows);
                  if (rows.length < pageSize) break;
                  offset += pageSize;
                }

                return all.map((e: any) => ({
                  ...e,
                  dias: calcularDiasAteVencimento(e.data_fim),
                  cliente_nome: e.cliente?.nome || (e.cliente_id ? `Cliente ${e.cliente_id}` : "—"),
                  tipo_label:
                    e.tipo === "renovacao_seguro"
                      ? formatAgendaCicloLabel(e.numero_ciclo)
                      : String(e.tipo || "")
                          .replace(/_/g, " ")
                          .replace(/\b\w/g, (l: string) => l.toUpperCase()),
                }));
              }}
            />
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <TooltipProvider delayDuration={200}>
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  {visibleAgendaCols.includes("id") && <th className={cn(listagemThCenter, "p-3 font-medium")}>ID</th>}
                  {visibleAgendaCols.includes("cliente") && <th className={cn(listagemThLeft, "p-3 font-medium")}>Cliente</th>}
                  {visibleAgendaCols.includes("venda") && <th className={cn(listagemThCenter, "p-3 font-medium")}>Venda</th>}
                  {visibleAgendaCols.includes("created_at") && <th className={cn(listagemThCenter, "p-3 font-medium")}>Data de criação</th>}
                  {visibleAgendaCols.includes("data_fim") && <th className={cn(listagemThCenter, "p-3 font-medium")}>Data Fim</th>}
                  {visibleAgendaCols.includes("dias") && <th className={cn(listagemThCenter, "p-3 font-medium")}>Dias até Vencimento</th>}
                  {visibleAgendaCols.includes("status") && <th className={cn(listagemThCenter, "p-3 font-medium")}>Status</th>}
                  <th className={cn(listagemThCenter, "p-3 font-medium")}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {eventos?.map((evento) => {
                  const diasAteVencimento = calcularDiasAteVencimento(evento.data_fim);
                  const tipoLabel = getTipoLabel(evento);
                  
                  return (
                    <tr key={evento.id} className="border-b hover:bg-gray-50">
                      {visibleAgendaCols.includes("id") && (
                        <td className={cn(listagemTdCenter, "p-3 text-gray-900 font-medium tabular-nums")}>
                          {evento.id}
                        </td>
                      )}
                      {visibleAgendaCols.includes("cliente") && (
                        <td className={cn(listagemTdLeft, "p-3 text-gray-900")}>
                          {evento.cliente?.nome || (evento.cliente_id ? `Cliente ${evento.cliente_id}` : "—")}
                        </td>
                      )}
                      {visibleAgendaCols.includes("venda") && (
                        <td className={cn(listagemTdCenter, "p-3")}>
                          <div className="flex justify-center">
                            <Badge className="bg-[#C9C9C9] text-gray-900 hover:bg-[#C9C9C9] border-transparent font-medium">
                              {tipoLabel}
                            </Badge>
                          </div>
                        </td>
                      )}
                      {visibleAgendaCols.includes("created_at") && (
                        <td className={cn(listagemTdCenter, "p-3 text-gray-600")}>
                          {formatDataBr(evento.created_at)}
                        </td>
                      )}
                      {visibleAgendaCols.includes("data_fim") && (
                        <td className={cn(listagemTdCenter, "p-3 text-gray-600")}>
                          {evento.data_fim ? new Date(evento.data_fim).toLocaleDateString("pt-BR") : "—"}
                        </td>
                      )}
                      {visibleAgendaCols.includes("dias") && (
                        <td className={cn(listagemTdCenter, "p-3")}>
                          {diasAteVencimento !== null ? (
                            <div className="flex justify-center">
                              <Badge
                                className={cn(
                                  diasAteVencimento < 0
                                    ? "bg-gray-800 text-white"
                                    : diasAteVencimento <= 7
                                      ? "bg-gray-700 text-white"
                                      : diasAteVencimento <= 30
                                        ? "bg-gray-200 text-gray-800"
                                        : "bg-gray-100 text-gray-700"
                                )}
                              >
                                <div className="flex items-center gap-1">
                                  {diasAteVencimento < 0 && <AlertTriangle className="h-3 w-3" />}
                                  {diasAteVencimento < 0
                                    ? `Vencido há ${Math.abs(diasAteVencimento)} dias`
                                    : diasAteVencimento === 0
                                      ? "Vence hoje"
                                      : `${diasAteVencimento} ${diasAteVencimento === 1 ? "dia" : "dias"}`}
                                </div>
                              </Badge>
                            </div>
                          ) : (
                            <span className="text-gray-400 text-sm">—</span>
                          )}
                        </td>
                      )}
                      {visibleAgendaCols.includes("status") && (
                        <td className={cn(listagemTdCenter, "p-3")}>
                          <div className="flex justify-center">
                            <Badge className={getStatusBadgeClass(evento.status)}>
                              {evento.status.charAt(0).toUpperCase() + evento.status.slice(1)}
                            </Badge>
                          </div>
                        </td>
                      )}
                      <td className={cn(listagemTdCenter, "p-3")}>
                          <div className="flex gap-1.5 justify-center">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => setEventoSelecionado(evento)}
                                >
                                  <Eye className="h-4 w-4" />
                                  <span className="sr-only">Ver</span>
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Ver</TooltipContent>
                            </Tooltip>
                            {evento.status === 'pendente' && evento.tipo === 'renovacao_seguro' && (() => {
                              const diasVenc = calcularDiasAteVencimento(evento.data_fim);
                              const liberado = podeIniciarRenovacao(evento.data_fim);
                              const msgBloqueio = mensagemRenovacaoIndisponivel(diasVenc);
                              return (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="inline-flex">
                                      <Button
                                        variant="outline"
                                        size="icon"
                                        className={cn(
                                          "h-8 w-8",
                                          liberado
                                            ? "bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100"
                                            : "opacity-60 cursor-not-allowed"
                                        )}
                                        disabled={!liberado}
                                        onClick={() => {
                                          if (!liberado) return;
                                          setEventoRenovacao(evento);
                                          setShowCriarRenovacao(true);
                                        }}
                                      >
                                        <RefreshCw className="h-4 w-4" />
                                        <span className="sr-only">Criar renovação</span>
                                      </Button>
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    {liberado ? "Criar renovação" : msgBloqueio}
                                  </TooltipContent>
                                </Tooltip>
                              );
                            })()}
                            {evento.status === 'pendente' && (
                              <>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="outline"
                                      size="icon"
                                      className="h-8 w-8 bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
                                      onClick={() => handleStatusChange(evento.id, 'concluido')}
                                    >
                                      <CheckCircle className="h-4 w-4" />
                                      <span className="sr-only">Concluir</span>
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Concluir lembrete</TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="outline"
                                      size="icon"
                                      className="h-8 w-8 bg-red-50 border-red-200 text-red-700 hover:bg-red-100"
                                      onClick={() => {
                                        setMotivoNaoRenovar("");
                                        setEventoNaoRenovar(evento);
                                      }}
                                    >
                                      <AlertTriangle className="h-4 w-4" />
                                      <span className="sr-only">Não renovar</span>
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Não renovar</TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="outline"
                                      size="icon"
                                      className="h-8 w-8 bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100"
                                      onClick={() => handleStatusChange(evento.id, 'cancelado')}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                      <span className="sr-only">Cancelar</span>
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Cancelar lembrete</TooltipContent>
                                </Tooltip>
                              </>
                            )}
                          </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </TooltipProvider>
            
            {eventos.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                {filtrosAplicados.urgencia === "proximas30"
                  ? "Nenhuma renovação nos próximos 30 dias"
                  : filtrosAplicados.urgencia === "urgentes7"
                    ? "Nenhuma renovação urgente nos próximos 7 dias"
                    : filtrosAplicados.urgencia === "vencidas"
                      ? "Nenhuma renovação vencida encontrada"
                      : "Nenhuma renovação de seguro encontrada"}
              </div>
            )}
            
            {Math.ceil(totalEventos / itemsPerPage) > 1 && (
              <div className="flex justify-center items-center gap-2 mt-4">
                <Button
                  variant="outline"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  Anterior
                </Button>
                <span className="text-gray-700">
                  Página {currentPage} de {Math.ceil(totalEventos / itemsPerPage)}
                </span>
                <Button
                  variant="outline"
                  onClick={() => setCurrentPage(prev => Math.min(Math.ceil(totalEventos / itemsPerPage), prev + 1))}
                  disabled={currentPage >= Math.ceil(totalEventos / itemsPerPage)}
                >
                  Próxima
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {!somenteEscopoVendedor && (
        <NovoEventoDialog open={showNovoEvento} onOpenChange={setShowNovoEvento} />
      )}
      
      {/* Modal para visualizar informações do pedido */}
      <VerPedidoModal 
        evento={eventoSelecionado}
        open={!!eventoSelecionado}
        onOpenChange={(open) => {
          if (!open) setEventoSelecionado(null);
        }}
      />

      <CriarPedidoRenovacaoDialog
        open={showCriarRenovacao}
        onOpenChange={(open) => {
          setShowCriarRenovacao(open);
          if (!open) setEventoRenovacao(null);
        }}
        evento={eventoRenovacao}
      />

      <Dialog open={!!eventoNaoRenovar} onOpenChange={(open) => {
        if (!open) {
          setEventoNaoRenovar(null);
          setMotivoNaoRenovar("");
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Não renovar seguro</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            O pedido sai da listagem de vigentes e vai para Não renovados. Informe o motivo.
          </p>
          <div>
            <Label htmlFor="motivo-nao-renovar">Motivo</Label>
            <Textarea
              id="motivo-nao-renovar"
              className="mt-1"
              rows={3}
              value={motivoNaoRenovar}
              onChange={(e) => setMotivoNaoRenovar(e.target.value)}
              placeholder="Ex.: cliente optou por outra corretora"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setEventoNaoRenovar(null)}>
              Voltar
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700"
              disabled={salvandoNaoRenovar || !motivoNaoRenovar.trim()}
              onClick={handleConfirmarNaoRenovar}
            >
              Confirmar não renovação
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
